/**
 * Scroll-layout body for WorldCupLeaderboard. Renders inside a parent
 * ScrollView (no FlashList) and handles both glass and compact row variants.
 * Skeleton-swaps the row list during Week ↔ Overall period changes so the
 * podium/chrome above stays put.
 */

import React, { Fragment } from 'react';
import { View } from 'react-native';
import { Skeleton } from '../../ui/Skeleton/Skeleton';
import { LeaderboardRow } from './LeaderboardRow';
import { LeaderboardGlassRowItem } from './LeaderboardGlassRowItem';
import type { LeaderboardTeamVisual } from './LeaderboardCard.constants';
import type { getWorldCupLeaderboardStyles } from './WorldCupLeaderboard.styles';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';
import type { WorldCupFuturesOptions } from '../../../src/worldcup/helpers/worldcupPicksData.helper';

type LeaderboardStyles = ReturnType<typeof getWorldCupLeaderboardStyles>;

interface LeaderboardScrollBodyProps {
  styles: LeaderboardStyles;
  rest: LeaderboardEntry[];
  glassList: LeaderboardEntry[];
  leaderboardGlass: boolean;
  isLeaderboardLoading: boolean;
  listHeader: React.ReactNode;
  listFooter: React.ReactNode;
  /** Rendered after the ranked rows and before the pinned footer (e.g. "Show more"). */
  listBottomContent?: React.ReactNode;
  futuresOptions: WorldCupFuturesOptions | null;
  /** Flags (national teams) or kits (clubs); defaults to flags (AC-3.6). */
  teamVisual?: LeaderboardTeamVisual;
  onLeaderboardRowPress?: (entry: LeaderboardEntry) => void;
  onOpenRowMenu?: (entry: LeaderboardEntry) => void;
  registerMenuTriggerRef?: (entryId: string, ref: View | null) => void;
  makeRegisterRowMenuRef: (entryId: string) => (ref: View | null) => void;
  onCurrentUserRowRef?: (ref: View | null) => void;
}

function RowsSkeleton({ styles }: { styles: LeaderboardStyles }) {
  return (
    <View style={styles.rowsSkeletonContainer}>
      <Skeleton width="100%" height={56} borderRadius={16} />
      <Skeleton width="100%" height={56} borderRadius={16} />
      <Skeleton width="100%" height={56} borderRadius={16} />
      <Skeleton width="100%" height={56} borderRadius={16} />
    </View>
  );
}

export function LeaderboardScrollBody({
  styles,
  rest,
  glassList,
  leaderboardGlass,
  isLeaderboardLoading,
  listHeader,
  listFooter,
  listBottomContent,
  futuresOptions,
  teamVisual = 'flag',
  onLeaderboardRowPress,
  onOpenRowMenu,
  registerMenuTriggerRef,
  makeRegisterRowMenuRef,
  onCurrentUserRowRef,
}: LeaderboardScrollBodyProps) {
  if (leaderboardGlass) {
    if (isLeaderboardLoading) {
      return (
        <View style={styles.leaderboardScrollBody}>
          {listHeader}
          <RowsSkeleton styles={styles} />
        </View>
      );
    }
    return (
      <View style={styles.leaderboardScrollBody}>
        {listHeader}
        {glassList.map((entry, index) => (
          <LeaderboardGlassRowItem
            key={entry.id}
            entry={entry}
            index={index}
            onPressRow={onLeaderboardRowPress}
            onOpenMenu={onOpenRowMenu}
            menuTriggerRef={registerMenuTriggerRef ? makeRegisterRowMenuRef(entry.id) : undefined}
            futuresOptions={futuresOptions}
            teamVisual={teamVisual}
            onCurrentUserRowRef={onCurrentUserRowRef}
          />
        ))}
        {listBottomContent}
        {listFooter}
      </View>
    );
  }

  return (
    <>
      {listHeader}
      {rest.map((entry, i) => (
        <Fragment key={entry.id}>
          <LeaderboardRow
            entry={entry}
            styles={styles}
            futuresOptions={futuresOptions}
            teamVisual={teamVisual}
          />
          {i < rest.length - 1 ? <View style={styles.separator} /> : null}
        </Fragment>
      ))}
      {listBottomContent}
      {listFooter}
    </>
  );
}
